/**
 * Freeze V2 — live embryo data for a single fertilization.
 *
 * The master grid renders detail panels through an AG Grid `detailCellRenderer`
 * that reads `embryosByFert` out of a stable ref, and the grid is configured with
 * `keepDetailRows`. Both are deliberate perf choices, but together they mean an
 * expanded detail panel is never re-rendered when the embryo query refetches, so
 * edits (freezer, storage location, ...) only appeared after a full page reload.
 *
 * Subscribing to the query cache here makes the detail panel re-render itself
 * when the data changes, independently of whether AG Grid re-invokes the
 * renderer.
 */
import { useCallback, useSyncExternalStore } from 'react';

import { useQueryClient } from '@tanstack/react-query';

import type { FertAndEmbryosResponse } from '@/services/freeze';

import { EMBRYO_QUERY_KEY } from './useFreezeData';

export function useLiveFertEmbryos(
  fertId: string | null | undefined,
): FertAndEmbryosResponse | undefined {
  const queryClient = useQueryClient();

  const subscribe = useCallback(
    (onStoreChange: () => void) =>
      queryClient.getQueryCache().subscribe(onStoreChange),
    [queryClient],
  );

  // Returns the cached entry object itself, so the identity stays stable while
  // the cache is unchanged. useSyncExternalStore requires that.
  const getSnapshot = useCallback(() => {
    if (!fertId) return undefined;

    const results = queryClient.getQueriesData<FertAndEmbryosResponse[]>({
      queryKey: [EMBRYO_QUERY_KEY],
    });

    for (let i = results.length - 1; i >= 0; i -= 1) {
      const match = results[i][1]?.find((entry) => entry.fertId === fertId);
      if (match) return match;
    }

    return undefined;
  }, [queryClient, fertId]);

  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}
